import * as React from 'react'
import styled from 'styled-components'

import { screenSizes } from '@src/styles/theme'

import type { ConnectionStatus } from '../types/admin'

export type OfflineBannerProps = {
  status: ConnectionStatus
  errorMessage?: string | null
  onRetry?: () => void
}

const Banner = styled.div<{ $tone: 'error' | 'info' }>`
  background-color: ${(p) => p.theme.modalBack};
  border: 1px solid ${(p) => (p.$tone === 'error' ? p.theme.warning : p.theme.accent)};
  border-radius: ${(p) => p.theme.radius};
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${(p) => p.theme.white};
  font-size: 14px;
  line-height: 1.4;

  @media (max-width: ${screenSizes.XS}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const Label = styled.span<{ $tone: 'error' | 'info' }>`
  color: ${(p) => (p.$tone === 'error' ? p.theme.warning : p.theme.accent)};
  font-family: 'NunitoSansSemiBold';
`

const Spacer = styled.div`
  flex: 1;
`

const RetryButton = styled.button`
  padding: 6px 14px;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  font-size: 13px;
  font-family: 'NunitoSansSemiBold';
  cursor: pointer;
  color: ${(p) => p.theme.white};
  min-height: 36px;
  background-color: ${(p) => p.theme.button.primary.default};

  &:hover {
    background-color: ${(p) => p.theme.button.primary.hover};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => p.theme.white}, 0 0 0 4px ${(p) => p.theme.accent};
    outline: none;
  }

  @media (max-width: ${screenSizes.XS}px) {
    width: 100%;
  }
`

function statusCopy(status: ConnectionStatus, errorMessage?: string | null): {
  tone: 'error' | 'info'
  label: string
  detail: string
} {
  if (status === 'error') {
    return {
      tone: 'error',
      label: 'Gateway offline.',
      detail: errorMessage
        ? `Showing last-known values. ${errorMessage}`
        : 'Showing last-known values. Destructive actions are disabled.',
    }
  }
  if (status === 'disconnected') {
    return {
      tone: 'info',
      label: 'Not connected.',
      detail: 'Equabot admin features are waiting on a gateway connection.',
    }
  }
  return {
    tone: 'info',
    label: 'Connecting…',
    detail: 'Establishing a gateway connection for this organization.',
  }
}

/**
 * Admin-wide offline banner (FR-017).
 * Renders nothing when `status === 'connected'`.
 */
export function OfflineBanner({ status, errorMessage, onRetry }: OfflineBannerProps) {
  if (status === 'connected') return null
  const { tone, label, detail } = statusCopy(status, errorMessage)
  return (
    <Banner $tone={tone} role="status" aria-live="polite">
      <Label $tone={tone}>{label}</Label>
      <span>{detail}</span>
      <Spacer />
      {onRetry ? <RetryButton onClick={onRetry}>Retry</RetryButton> : null}
    </Banner>
  )
}
